import { useTranslation } from "react-i18next";
import { useMemo } from "react";

import { SummaryCard } from "@/pages/Dashboard/components/SummaryCard";
import { Invoice } from "@/api/opb/opbTypes";
import { formatCurrency } from "@/utils/string";
import { isInvoiceLate } from "@/utils/invoice";

interface CurrentBalanceProps {
  invoices: Invoice[];
  isLoading: boolean;
  numLocations: number;
  currency: string;
}

export function CurrentBalance({
  invoices,
  isLoading,
  numLocations,
  currency,
}: CurrentBalanceProps) {
  const { t } = useTranslation("dashboard");

  const unpaidInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.balance > 0),
    [invoices]
  );

  const totalBalance = useMemo(
    () =>
      unpaidInvoices.reduce((balance, invoice) => balance + invoice.balance, 0),
    [unpaidInvoices]
  );

  const countOverDueInvoices = useMemo(
    () => unpaidInvoices.filter((i) => isInvoiceLate(i)).length,
    [unpaidInvoices]
  );

  const getDescription = () => {
    if (unpaidInvoices.length === 0) {
      return t("No invoices due");
    }

    return `${t(
      countOverDueInvoices ? "You have invoices overdue" : "Invoices due soon",
      { count: countOverDueInvoices || unpaidInvoices.length }
    )}${t("at location", {
      count: numLocations,
    })}`;
  };

  return (
    <SummaryCard
      title={t("Current Balance", { currency })}
      data-testid="current_balance-card"
      message={`${formatCurrency({
        value: totalBalance,
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} (${currency})`}
      description={getDescription()}
      isLoading={isLoading}
    />
  );
}
